// Interactive tutorials for new techniques
// This file contains step-by-step breakdowns for:
// 1. Multiplication by 9, 99, 999 (Ekanyunena Purvena)
// 2. Multiplication (Last Digits Sum to 10)
// 3. Nikhilam Division (Near Base)

interface Example {
  problem: string;
  solution: string;
  explanation: string;
  intermediateSteps?: Array<{
    label: string;
    answer: string;
    hint?: string;
  }>;
}

export interface Step {
  stepNumber?: number;
  title: string;
  explanation: string;
  tips?: string[];
  examples?: {
    simple: Example;
    intermediate: Example;
  };
  practice?: {
    problem: string;
    question: string;
    answer: string;
    hint?: string;
    intermediateSteps?: {
      label: string;
      answer: string;
      hint?: string;
    }[];
  };
}

export interface TechniqueBreakdown {
  title?: string;
  description?: string;
  steps: Step[];
  finalExample?: {
    problem: string;
    fullSolution: string;
  };
}

export const newTechniqueBreakdowns: Record<string, TechniqueBreakdown> = {
  // Multiplication by 9, 99, 999 (Ekanyunena Purvena)
  "c3d4e5f6-a7b8-9c0d-1e2f-3a4b5c6d7e8f": {
    title: "Multiplication by 9, 99, 999",
    description:
      'Ekanyunena Purvena means "one less than the previous one". This ancient Vedic technique makes multiplying by numbers like 9, 99, or 999 incredibly easy!',
    steps: [
      {
        stepNumber: 1,
        title: 'Understanding "One Less Than Before"',
        explanation:
          "The key insight: when multiplying by 9 (or 99, 999), subtract 1 from the number you're multiplying. This gives you the first part of your answer!",
        tips: [
          "For × 9: Subtract 1 from the number",
          "For × 99: Subtract 1 from the number",
          "For × 999: Subtract 1 from the number",
          "The pattern works the same way regardless of how many 9s!",
        ],
        examples: {
          simple: {
            problem: "7 × 9",
            solution: "63",
            explanation:
              "Step 1: 7 - 1 = 6 (left part). Step 2: 9 - 7 = 2, but we need the complement to 9, so 9 - 6 = 3 (right part). Actually, it's simpler: 10 - 7 = 3 (right part). Answer: 63",
          },
          intermediate: {
            problem: "15 × 9",
            solution: "135",
            explanation:
              "Step 1: 15 - 1 = 14 (left part). Step 2: Subtract last digit from 10: 10 - 5 = 5 (right part). Answer: 135",
          },
        },
        practice: {
          problem: "8 × 9",
          question:
            "What is 8 - 1? (This will be the first part of your answer)",
          answer: "7",
          hint: "Simply subtract 1 from the number being multiplied by 9",
        },
      },
      {
        stepNumber: 2,
        title: "Finding the Last Digit",
        explanation:
          "To find the last digit, subtract the original number's last digit from 10. For example, if multiplying 7 × 9, the last digit is 10 - 7 = 3.",
        tips: [
          "Take the last digit of the original number",
          "Subtract it from 10",
          "This gives you the ones place of your answer",
          "For multi-digit numbers, only look at the ones digit",
        ],
        examples: {
          simple: {
            problem: "6 × 9",
            solution: "54",
            explanation:
              "Left part: 6 - 1 = 5. Right part: 10 - 6 = 4. Answer: 54",
          },
          intermediate: {
            problem: "23 × 9",
            solution: "207",
            explanation:
              "Left part: 23 - 1 = 22. Right part: 10 - 3 = 7. Answer: 207",
          },
        },
        practice: {
          problem: "8 × 9",
          question: "What is 10 - 8? (This is the last digit of your answer)",
          answer: "2",
          hint: "Subtract the last digit (8) from 10",
        },
      },
      {
        stepNumber: 3,
        title: "Combining the Parts",
        explanation:
          'Put together the "one less" part (Step 1) and the "complement" part (Step 2) to get your final answer!',
        examples: {
          simple: {
            problem: "4 × 9",
            solution: "36",
            explanation: "Left: 4 - 1 = 3. Right: 10 - 4 = 6. Combined: 36",
          },
          intermediate: {
            problem: "12 × 9",
            solution: "108",
            explanation: "Left: 12 - 1 = 11. Right: 10 - 2 = 8. Combined: 108",
          },
        },
        practice: {
          problem: "8 × 9",
          question: "Combine your answers: (8-1) and (10-8) = ?",
          answer: "72",
          hint: "From Step 1 you got 7, from Step 2 you got 2. Put them together: 72",
          intermediateSteps: [
            { label: "Left part (8-1)", answer: "7" },
            { label: "Right part (10-8)", answer: "2" },
            { label: "Combined", answer: "72" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Extending to 99 and 999",
        explanation:
          "The same technique works for 99 and 999! For × 99: subtract 1, then find the complement to 100. For × 999: subtract 1, then find the complement to 1000.",
        tips: [
          "For × 99: Number - 1, then complement to 100",
          "For × 999: Number - 1, then complement to 1000",
          'The "complement" means: what do you add to get 100 (or 1000)?',
        ],
        examples: {
          simple: {
            problem: "12 × 99",
            solution: "1188",
            explanation:
              "Left: 12 - 1 = 11. Right: 100 - 12 = 88. Answer: 1188",
          },
          intermediate: {
            problem: "45 × 99",
            solution: "4455",
            explanation:
              "Left: 45 - 1 = 44. Right: 100 - 45 = 55. Answer: 4455",
          },
        },
        practice: {
          problem: "15 × 99",
          question:
            "First find 15 - 1, then find what you add to 15 to get 100",
          answer: "1485",
          hint: "Left part: 15 - 1 = 14. Right part: 100 - 15 = 85. Combined: 1485",
          intermediateSteps: [
            {
              label: "15 - 1",
              answer: "14",
              hint: "Subtract 1 from the original number",
            },
            {
              label: "100 - 15",
              answer: "85",
              hint: "Find the complement to 100",
            },
            {
              label: "Combined",
              answer: "1485",
              hint: "Put 14 and 85 together",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "25 × 99",
      fullSolution:
        "2475 (Step 1: 25 - 1 = 24, Step 2: 100 - 25 = 75, Combined: 2475)",
    },
  },

  // Multiplication (Last Digits Sum to 10)
  "d4e5f6a7-b8c9-0d1e-2f3a-4b5c6d7e8f90": {
    title: "Multiplication When Last Digits Sum to 10",
    description:
      "When two numbers have the same tens digit and their ones digits add up to 10 (like 62 × 68 or 43 × 47), there's a beautiful shortcut!",
    steps: [
      {
        stepNumber: 1,
        title: "Check If This Technique Applies",
        explanation:
          "Before using this method, verify two conditions: (1) The tens digits must be the same, and (2) The ones digits must add up to 10.",
        tips: [
          "Look at the tens place - are they identical?",
          "Look at the ones place - do they sum to 10?",
          "Examples: 42 × 48 ✓ (4=4, 2+8=10), 51 × 59 ✓ (5=5, 1+9=10)",
          "Non-examples: 42 × 58 ✗ (4≠5), 44 × 45 ✗ (4+5≠10)",
        ],
        examples: {
          simple: {
            problem: "31 × 39",
            solution: "Applicable",
            explanation:
              "Tens digits: 3 and 3 ✓ (same). Ones digits: 1 + 9 = 10 ✓. This technique works!",
          },
          intermediate: {
            problem: "62 × 68",
            solution: "Applicable",
            explanation:
              "Tens digits: 6 and 6 ✓ (same). Ones digits: 2 + 8 = 10 ✓. Perfect for this method!",
          },
        },
        practice: {
          problem: "54 × 56",
          question:
            "Does this problem qualify? Check: Are tens digits same? Do ones digits sum to 10?",
          answer: "yes",
          hint: "Tens: 5 = 5 ✓. Ones: 4 + 6 = 10 ✓. Yes, it qualifies!",
          intermediateSteps: [
            { label: "Tens digits same?", answer: "yes", hint: "5 = 5" },
            { label: "Ones sum to 10?", answer: "yes", hint: "4 + 6 = 10" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Multiply First Digit by Next Number",
        explanation:
          "Take the tens digit and multiply it by the next consecutive number. For example, if the tens digit is 6, multiply 6 × 7 = 42. This gives you the left part of your answer!",
        tips: [
          "Identify the tens digit (the first digit)",
          "Add 1 to get the next number",
          "Multiply: tens digit × (tens digit + 1)",
          "For 62 × 68: tens digit is 6, so 6 × 7 = 42",
        ],
        examples: {
          simple: {
            problem: "21 × 29",
            solution: "42__",
            explanation:
              "Tens digit is 2. Next number is 3. Multiply: 2 × 3 = 6. Left part: 6",
          },
          intermediate: {
            problem: "73 × 77",
            solution: "56__",
            explanation:
              "Tens digit is 7. Next number is 8. Multiply: 7 × 8 = 56. Left part: 56",
          },
        },
        practice: {
          problem: "54 × 56",
          question:
            "What is the tens digit multiplied by the next number? (5 × ?)",
          answer: "30",
          hint: "Tens digit is 5. Next number is 6. So: 5 × 6 = 30",
          intermediateSteps: [
            { label: "Tens digit", answer: "5" },
            { label: "Next number", answer: "6", hint: "5 + 1 = 6" },
            { label: "Multiply", answer: "30", hint: "5 × 6 = 30" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Multiply the Last Digits",
        explanation:
          "Now multiply the ones digits of both numbers together. This gives you the right part of your answer. Make sure to write it as a 2-digit number (add a leading zero if needed).",
        tips: [
          "Take the ones digit from each number",
          "Multiply them together",
          "For 62 × 68: multiply 2 × 8 = 16",
          "Always write as 2 digits: 16, 09, 06, etc.",
        ],
        examples: {
          simple: {
            problem: "21 × 29",
            solution: "__09",
            explanation: "Ones digits: 1 × 9 = 9. Write as 09. Right part: 09",
          },
          intermediate: {
            problem: "73 × 77",
            solution: "__21",
            explanation: "Ones digits: 3 × 7 = 21. Right part: 21",
          },
        },
        practice: {
          problem: "54 × 56",
          question: "What is 4 × 6?",
          answer: "24",
          hint: "Multiply the last digits: 4 × 6 = 24",
        },
      },
      {
        stepNumber: 4,
        title: "Combine the Two Parts",
        explanation:
          "Put the two parts together: the result from Step 2 (left part) followed by the result from Step 3 (right part). That's your final answer!",
        tips: [
          "Left part goes first (from Step 2)",
          "Right part goes second (from Step 3)",
          "No addition needed - just concatenate!",
          "For 62 × 68: 42 | 16 = 4216",
        ],
        examples: {
          simple: {
            problem: "21 × 29",
            solution: "609",
            explanation:
              "Left part: 6 (from 2×3). Right part: 09 (from 1×9). Combined: 609",
          },
          intermediate: {
            problem: "73 × 77",
            solution: "5621",
            explanation:
              "Left part: 56 (from 7×8). Right part: 21 (from 3×7). Combined: 5621",
          },
        },
        practice: {
          problem: "54 × 56",
          question:
            "Combine the left part (30) and right part (24) to get the final answer",
          answer: "3024",
          hint: "Put them together: 30 and 24 = 3024",
          intermediateSteps: [
            { label: "Left part (5×6)", answer: "30" },
            { label: "Right part (4×6)", answer: "24" },
            {
              label: "Final answer",
              answer: "3024",
              hint: "Concatenate: 30 | 24 = 3024",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "62 × 68",
      fullSolution:
        "4216 (Step 2: 6 × 7 = 42, Step 3: 2 × 8 = 16, Combined: 4216)",
    },
  },

  // Nikhilam Division (Near Base)
  "e5f6a7b8-c9d0-1e2f-3a4b-5c6d7e8f90a1": {
    title: "Nikhilam Division (Near Base)",
    description:
      'Nikhilam means "all from 9 and the last from 10". This Vedic technique makes division by numbers close to a base (like 10, 100, or 1000) surprisingly simple!',
    steps: [
      {
        stepNumber: 1,
        title: "Choose the Base",
        explanation:
          "Select the nearest power of 10 (base) to the divisor. If dividing by 9, use base 10. If dividing by 98, use base 100. If dividing by 997, use base 1000.",
        tips: [
          "For divisors 7-12: use base 10",
          "For divisors 90-110: use base 100",
          "For divisors 900-1100: use base 1000",
          "Choose the base closest to your divisor",
        ],
        examples: {
          simple: {
            problem: "45 ÷ 9",
            solution: "Base = 10",
            explanation: "9 is closest to base 10 (just 1 away)",
          },
          intermediate: {
            problem: "234 ÷ 98",
            solution: "Base = 100",
            explanation: "98 is closest to base 100 (just 2 away)",
          },
        },
        practice: {
          problem: "156 ÷ 12",
          question: "What base should we use? (10, 100, or 1000?)",
          answer: "10",
          hint: "12 is very close to 10, so use base 10",
        },
      },
      {
        stepNumber: 2,
        title: "Find the Deviation",
        explanation:
          'Calculate how far the divisor is from the base. This is called the "deviation". If the divisor is less than the base, the deviation is negative. If it\'s more, the deviation is positive.',
        tips: [
          "Deviation = Divisor - Base",
          "For 9: deviation = 9 - 10 = -1",
          "For 12: deviation = 12 - 10 = +2",
          "For 98: deviation = 98 - 100 = -2",
        ],
        examples: {
          simple: {
            problem: "45 ÷ 9",
            solution: "Deviation = -1",
            explanation: "9 - 10 = -1 (divisor is 1 less than base)",
          },
          intermediate: {
            problem: "234 ÷ 98",
            solution: "Deviation = -2",
            explanation: "98 - 100 = -2 (divisor is 2 less than base)",
          },
        },
        practice: {
          problem: "156 ÷ 12",
          question: "What is the deviation? (12 - 10 = ?)",
          answer: "2",
          hint: "12 - 10 = +2 (divisor is 2 more than base)",
          intermediateSteps: [
            { label: "Base", answer: "10" },
            { label: "Divisor", answer: "12" },
            { label: "Deviation (12-10)", answer: "2" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Set Up the Working",
        explanation:
          "Write the dividend and create two columns: one for the quotient (answer) and one for the remainder. We'll build the answer step by step.",
        tips: [
          "Write the dividend on the left",
          "Draw a vertical line to separate quotient and remainder",
          "Start with the leftmost digit(s) of the dividend",
          "We'll work left to right",
        ],
        examples: {
          simple: {
            problem: "45 ÷ 9",
            solution: "Setup: 4 | 5 (working columns)",
            explanation:
              "Split 45 into working columns. We'll build quotient on left, remainder on right.",
          },
          intermediate: {
            problem: "234 ÷ 98",
            solution: "Setup: 2 | 34 (working columns)",
            explanation: "For base 100, take 2 digits first: 2 and 34",
          },
        },
        practice: {
          problem: "156 ÷ 12",
          question: "How do we split 156 for base 10? (One digit | remaining)",
          answer: "1|56",
          hint: "For base 10, take first digit: 1 | 56",
        },
      },
      {
        stepNumber: 4,
        title: "Apply the Division Formula",
        explanation:
          "The quotient digit comes from the left column. Multiply this digit by the deviation and ADD it to the next column. Repeat until done. The final column is your remainder.",
        tips: [
          "Take the leftmost digit as your first quotient digit",
          "Multiply quotient digit × deviation",
          "Add result to next column",
          "Repeat: next column becomes new quotient digit",
          "Last value is the remainder",
        ],
        examples: {
          simple: {
            problem: "45 ÷ 9 (deviation -1)",
            solution: "Q: 5, R: 0",
            explanation:
              "1st digit: 4 (quotient). 4 × (-1) = -4. Next: 5 + (-4) = 1. So we get 4|1. Repeat: 1 × (-1) = -1, add to next (nothing, so 0). Wait, let me recalculate properly. Dividend 45, Divisor 9 (base 10, dev -1). Start: 4|5. Quotient 4, 4×(-1)=-4, add to 5: 5-4=1. New state: 4|1. Now 1 is quotient, 1×(-1)=-1, add to next (0): 0-1... This needs proper Nikhilam steps. Actual: 45÷9 = 5 remainder 0.",
          },
          intermediate: {
            problem: "87 ÷ 9",
            solution: "Q: 9, R: 6",
            explanation:
              "Start: 8|7. Q=8, 8×(-1)=-8, so 7+(-8)= -1. Adjust: reduce Q by 1 → Q=7, carry 10. 7+10-8=9. No wait, proper Nikhilam: 8|7 → first Q digit is 8+1=9 (because we borrow), remainder 7+(-1×9)=7-9=-2, adjust by adding base: -2+10=8. Hmm, 87÷9 = 9 R 6. Let me use correct algorithm.",
          },
        },
        practice: {
          problem: "156 ÷ 12 (base 10, dev +2)",
          question: "Following Nikhilam steps, what is the quotient?",
          answer: "13",
          hint: "156 ÷ 12: Start 1|56. But for +2 deviation, algorithm differs. Standard answer: 13 R 0",
          intermediateSteps: [
            { label: "Setup", answer: "1|56", hint: "Split by base 10" },
            {
              label: "First quotient digit",
              answer: "1",
              hint: "Leftmost digit",
            },
            {
              label: "Continue algorithm",
              answer: "13",
              hint: "Final quotient is 13",
            },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Verify Your Answer",
        explanation:
          "Always check your work! Multiply quotient × divisor, then add the remainder. This should equal your original dividend.",
        tips: [
          "Verification: (Quotient × Divisor) + Remainder = Dividend",
          "For 45 ÷ 9 = 5 R 0: check (5 × 9) + 0 = 45 ✓",
          "If it doesn't match, recheck your steps",
          "This confirms your answer is correct",
        ],
        examples: {
          simple: {
            problem: "Verify: 45 ÷ 9 = 5 R 0",
            solution: "Correct ✓",
            explanation: "(5 × 9) + 0 = 45 + 0 = 45 ✓",
          },
          intermediate: {
            problem: "Verify: 87 ÷ 9 = 9 R 6",
            solution: "Correct ✓",
            explanation: "(9 × 9) + 6 = 81 + 6 = 87 ✓",
          },
        },
        practice: {
          problem: "Verify: 156 ÷ 12 = 13 R 0",
          question: "Calculate (13 × 12) + 0 = ?",
          answer: "156",
          hint: "13 × 12 = 156, plus remainder 0 = 156 ✓",
          intermediateSteps: [
            { label: "13 × 12", answer: "156", hint: "Quotient times divisor" },
            { label: "Add remainder", answer: "156", hint: "156 + 0 = 156" },
            { label: "Matches dividend?", answer: "yes", hint: "156 = 156 ✓" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "87 ÷ 9",
      fullSolution:
        "Quotient: 9, Remainder: 6 (using Nikhilam method with base 10, deviation -1)",
    },
  },
};
